import type { PolygonIndex } from '../../geo/polygonIndex';
import type { OBB, Vec2 } from '../../geo/types';
import { obbCorners } from '../../geo/polygon';
import { obbOverlap } from '../../geo/sat';
import type { Lot, ReservedFootprint } from './types';
import { LOT } from './lotRules';
import { obbToAABB, obbsMayMeet } from './lotObb';
import { createCellGrid } from './cellGrid';
import { createReservedIndex } from './reservedIndex';

/** Where buildings behind the houses may stand, knowing the lots already standing. */
export interface LotPlacer {
  /** Whether a building fits: on buildable ground, clear of every lot so far and of every reservation's outline. */
  fits(obb: OBB): boolean;
  /** The corners of the lots a building would overlap, in the order they were placed; reservations are not asked. */
  blockers(obb: OBB): readonly (readonly Vec2[])[];
  add(lot: Lot): void;
}

interface Filed {
  readonly lot: Lot;
  readonly order: number;
}

export function createLotPlacer(
  buildable: (p: Vec2) => boolean,
  lots: readonly Lot[],
  reserved: readonly ReservedFootprint[],
  buildableIndex?: Pick<PolygonIndex, 'obbInside'>
): LotPlacer {
  const grid = createCellGrid<Filed>(16);
  const reservedIndex = createReservedIndex(reserved);
  let placed = 0;
  const add = (lot: Lot): void => {
    grid.insert({ lot, order: placed++ }, obbToAABB(lot.obb));
  };
  lots.forEach(add);
  const meets = (obb: OBB, other: Lot) =>
    obbsMayMeet(obb, other.obb) && obbOverlap(obb, other.obb, LOT.overlapTolerance);
  return {
    fits: (obb) =>
      (buildableIndex ? buildableIndex.obbInside(obb) : obbCorners(obb).every(buildable)) &&
      !grid.some(obbToAABB(obb), (filed) => meets(obb, filed.lot)) &&
      !reservedIndex.overlaps(obb),
    blockers: (obb) => {
      const found = new Set<Filed>();
      grid.some(obbToAABB(obb), (filed) => {
        if (meets(obb, filed.lot)) found.add(filed);
        return false;
      });
      return [...found].sort((a, b) => a.order - b.order).map((filed) => filed.lot.corners);
    },
    add,
  };
}
